'use client';

import { Content } from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { ArrowRightIcon } from '@/assets';

/**
 * Props for `CtaMapText`.
 */
export type CtaMapTextProps = SliceComponentProps<Content.CtaMapTextSlice>;

/**
 * Component for "CtaMapText" Slices.
 */
const CtaMapText = ({ slice }: CtaMapTextProps): JSX.Element => {
  // Handle the with_cta_map_left_text variation
  if (slice.variation === 'with_cta_map_left_text') {
    const { title, description, cta_link, button_label, map_visual } =
      slice.primary;

    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="relative text-white py-16 md:py-24"
        style={{
          background: 'linear-gradient(180deg, #0E1110 0%, #0F423A 53.29%)',
        }}
      >
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text Content */}
            <div className="space-y-6">
              {title && (
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
                  <PrismicRichText field={title} />
                </h2>
              )}

              {description && (
                <div className="text-lg md:text-xl text-gray-200 leading-relaxed">
                  <PrismicRichText field={description} />
                </div>
              )}

              {cta_link && (
                <div className="pt-4">
                  <PrismicNextLink
                    field={cta_link}
                    className="secondary-button self-center md:self-start relative pr-10 group w-auto max-w-fit"
                    style={{ minWidth: '160px' }}
                  >
                    <span
                      className="absolute left-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      style={{ pointerEvents: 'none' }}
                    >
                      <ArrowRightIcon className="arrow-icon" />
                    </span>
                    <span className="pl-6 pr-2">
                      {button_label || cta_link.text || 'Apply now'}
                    </span>
                  </PrismicNextLink>
                </div>
              )}
            </div>

            {/* Right Column - Map Visual */}
            <div className="relative">
              {map_visual ? (
                <div className="relative">
                  <PrismicNextImage
                    field={map_visual}
                    className="w-full h-auto max-w-lg mx-auto"
                    fallbackAlt=""
                  />
                </div>
              ) : (
                /* Fallback: Custom Americas Map with Dots */
                <div className="relative w-full max-w-lg mx-auto h-96">
                  <svg
                    viewBox="0 0 400 400"
                    className="w-full h-full"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    {/* Americas Outline with Dots */}
                    <g className="opacity-60">
                      {/* North America dots */}
                      {Array.from({ length: 80 }, (_, i) => {
                        const x = 50 + (i % 10) * 15 + Math.random() * 10;
                        const y =
                          20 + Math.floor(i / 10) * 12 + Math.random() * 8;
                        return (
                          <circle
                            key={`na-${i}`}
                            cx={x}
                            cy={y}
                            r="2"
                            fill="#6B7280"
                            className="animate-pulse"
                            style={{ animationDelay: `${i * 0.1}s` }}
                          />
                        );
                      })}

                      {/* Central America dots */}
                      {Array.from({ length: 20 }, (_, i) => {
                        const x = 80 + (i % 5) * 8 + Math.random() * 6;
                        const y =
                          140 + Math.floor(i / 5) * 8 + Math.random() * 4;
                        return (
                          <circle
                            key={`ca-${i}`}
                            cx={x}
                            cy={y}
                            r="2"
                            fill="#10B981"
                            className="animate-pulse"
                            style={{ animationDelay: `${i * 0.15}s` }}
                          />
                        );
                      })}

                      {/* South America dots */}
                      {Array.from({ length: 120 }, (_, i) => {
                        const x = 120 + (i % 12) * 12 + Math.random() * 8;
                        const y =
                          180 + Math.floor(i / 12) * 15 + Math.random() * 10;
                        return (
                          <circle
                            key={`sa-${i}`}
                            cx={x}
                            cy={y}
                            r="2"
                            fill={Math.random() > 0.7 ? '#10B981' : '#6B7280'}
                            className="animate-pulse"
                            style={{ animationDelay: `${i * 0.08}s` }}
                          />
                        );
                      })}
                    </g>
                  </svg>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Fallback for other variations
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="py-16 bg-gray-100"
    >
      <div className="container mx-auto px-4 text-center">
        <p>CtaSideImg slice variation: {slice.variation}</p>
      </div>
    </section>
  );
};

export default CtaMapText;
